import type { DateRange, AttributionFilterState } from '@/components/analytics';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createFileRoute } from '@tanstack/react-router';
import { useOrganization } from '@/context/organization-context';
import { ConnectIntegrationsEmptyState } from '@/components/dashboard/empty-states';
import { CampaignInsights } from '@/sections/campaign-audit/campaign-audit-view';
import { ProgramAuditSummary } from '@/sections/program-audit/program-audit-view';
import { FEATURE_FLAGS, useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useFlowAnalyticsControllerGetFlowOverview } from '@/apis/backend/generated/analytics-flows/analytics-flows';
import { useCampaignAnalyticsControllerGetCampaignOverview } from '@/apis/backend/generated/analytics-campaigns/analytics-campaigns';
import { useAnalyticsControllerGetListGrowth , useAnalyticsControllerGetRevenueAnalytics } from '@/apis/backend/generated/analytics/analytics';
import {
  MetricCard,
  PeriodSelector,
  TimeSeriesChart,
  autoGranularity,
  computeRprMetric,
  AttributionControl,
  useDefaultDateRange,
} from '@/components/analytics';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Card from '@mui/material/Card';
import Tabs from '@mui/material/Tabs';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';

export const Route = createFileRoute('/_authed/dashboard/o/$orgId/c/$clientId/analytics/')({
  component: AnalyticsOverviewPage,
});

function SectionSkeleton({ cols = 3, height = 120 }: { cols?: number; height?: number }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 2 }}>
      {Array.from({ length: cols }, (_, i) => (
        <Skeleton key={i} variant="rounded" height={height} sx={{ borderRadius: '12px' }} />
      ))}
    </Box>
  );
}

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card
      sx={{
        p: 3,
        bgcolor: 'background.paper',
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'grey.200',
      }}
    >
      <Stack spacing={2}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, letterSpacing: '-0.005em' }}>
          {title}
        </Typography>
        {children}
      </Stack>
    </Card>
  );
}

function AnalyticsOverviewPage() {
  const { t } = useTranslation('analytics');
  const { activeOrganization, activeClient } = useOrganization();
  const defaultRange = useDefaultDateRange(30);
  const [dateRange, setDateRange] = useState<DateRange>(defaultRange);
  const [attribution, setAttribution] = useState<AttributionFilterState>({});
  const [analyticsTab, setAnalyticsTab] = useState(0);

  const orgId = activeOrganization?.id;
  const clientId = activeClient?.id;

  const programAuditEnabled = useFeatureFlag(FEATURE_FLAGS.PROGRAM_AUDIT);
  const campaignAuditEnabled = useFeatureFlag(FEATURE_FLAGS.CAMPAIGN_AUDIT);
  // The Insights tab only has content when at least one of its features is on
  // for the org; hide the whole tab row otherwise so flag-off orgs don't see
  // an empty tab.
  const insightsEnabled = programAuditEnabled || campaignAuditEnabled;

  const granularity = autoGranularity(dateRange.startDate, dateRange.endDate);

  // Charts always show the last 12 months of context, independent of the
  // selected period. The period selector only drives the metric-card numbers
  // and their previous-period comparison.
  const chartRange = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 12);
    return {
      startDate: start.toISOString().slice(0, 10),
      endDate: end.toISOString().slice(0, 10),
    };
  }, []);

  const sharedParams = {
    orgId: orgId!,
    clientId: clientId!,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    previousStartDate: dateRange.previousStartDate,
    previousEndDate: dateRange.previousEndDate,
    ...attribution,
  };

  const chartParams = {
    orgId: orgId!,
    clientId: clientId!,
    startDate: chartRange.startDate,
    endDate: chartRange.endDate,
    granularity: 'month' as const,
    ...attribution,
  };

  const queryOpts = { query: { enabled: !!orgId && !!clientId, staleTime: 60_000 } } as const;
  // Chart queries are pinned to a rolling 12-month window — they don't move
  // when the user changes the period selector, so cache them aggressively.
  const chartQueryOpts = {
    query: { enabled: !!orgId && !!clientId, staleTime: 5 * 60_000, gcTime: 30 * 60_000 },
  } as const;

  // Period-scoped queries — drive metric cards (with previous-period comparison).
  const revenue = useAnalyticsControllerGetRevenueAnalytics({ ...sharedParams, granularity }, queryOpts);
  const campaigns = useCampaignAnalyticsControllerGetCampaignOverview(
    { ...sharedParams, granularity },
    queryOpts,
  );
  const flows = useFlowAnalyticsControllerGetFlowOverview(
    { ...sharedParams, granularity },
    queryOpts,
  );
  const listGrowth = useAnalyticsControllerGetListGrowth(
    { ...sharedParams, granularity },
    queryOpts,
  );

  // 12-month chart queries — drive the time-series visualizations only.
  const revenueChart = useAnalyticsControllerGetRevenueAnalytics(chartParams, chartQueryOpts);
  const campaignsChart = useCampaignAnalyticsControllerGetCampaignOverview(chartParams, chartQueryOpts);
  const flowsChart = useFlowAnalyticsControllerGetFlowOverview(chartParams, chartQueryOpts);
  const listGrowthChart = useAnalyticsControllerGetListGrowth(chartParams, chartQueryOpts);

  const rev = revenue.data;
  const camp = campaigns.data;
  const flow = flows.data;
  const growth = listGrowth.data;

  const revChart = revenueChart.data;
  const campChart = campaignsChart.data;
  const flowChart = flowsChart.data;
  const growthChart = listGrowthChart.data;

  const anyError = !!(revenue.error || campaigns.error || flows.error || listGrowth.error);

  const attributedRevenueSeries = useMemo(() => {
    if (!revChart) return [];
    return revChart.timeSeries.map((p) => ({
      date: p.date,
      revenue: p.revenue,
      percentAttributed:
        p.totalStoreRevenue > 0 ? Math.round((p.revenue / p.totalStoreRevenue) * 1000) / 10 : 0,
    }));
  }, [revChart]);

  // Derive per-bucket Email vs SMS attributed revenue. Campaigns don't carry a
  // per-bucket channel split, so we approximate Email = campaign + flow email,
  // SMS = flow sms. This matches the totals on the metric cards and is close
  // enough for the time-series shape.
  const channelRevenueSeries = useMemo(() => {
    if (!flowChart) return [];
    const campByDate = new Map<string, number>();
    for (const p of campChart?.timeSeries ?? []) campByDate.set(p.date, p.revenue);
    return flowChart.timeSeries.map((p) => ({
      date: p.date,
      emailRevenue: (campByDate.get(p.date) ?? 0) + p.emailRevenue,
      smsRevenue: p.smsRevenue,
    }));
  }, [campChart, flowChart]);

  // Chart x-axis always renders monthly labels since the chart range is 12 months.
  const formatBucketLabel = useMemo(() => (dateStr: string) => {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    }, []);

  if (!orgId || !clientId) return null;

  return (
    <Stack spacing={4}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ md: 'center' }}
        justifyContent="space-between"
      >
        <PeriodSelector value={dateRange} onChange={setDateRange} />
        <AttributionControl value={attribution} onChange={setAttribution} />
      </Stack>

      {/* Split the facts (Overview) from the AI judgment layer (Insights) so the
          long overview isn't buried under the teardown, and vice versa. */}
      {insightsEnabled && (
        <Tabs
          value={analyticsTab}
          onChange={(_, v) => setAnalyticsTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={t('analytics.tabs.overview', 'Overview')} sx={{ textTransform: 'none' }} />
          <Tab label={t('analytics.tabs.insights', 'Insights')} sx={{ textTransform: 'none' }} />
        </Tabs>
      )}

      {insightsEnabled && analyticsTab === 1 && (
        <Stack spacing={2.5}>
          {programAuditEnabled && <ProgramAuditSummary orgId={orgId} clientId={clientId} />}
          {campaignAuditEnabled && <CampaignInsights orgId={orgId} clientId={clientId} />}
        </Stack>
      )}

      {(!insightsEnabled || analyticsTab === 0) && (
      <Stack spacing={4}>
      {anyError && (
        <Typography color="error" variant="body2">
          {t('analytics.errorLoadFailed', 'Some data failed to load. Check that the API server has been restarted.')}
        </Typography>
      )}

      {/* -- Revenue sections: gated on attribution-capable provider. When the
            client has no Klaviyo (or equivalent placedOrderEvents source), the
            backend returns available=false and we replace ALL revenue blocks
            with a single connect-integration CTA. Campaign/flow/list-growth
            sections below have independent data sources and keep rendering. -- */}
      {rev && !rev.available ? (
        <ConnectIntegrationsEmptyState
          title={t('analytics.connectKlaviyoTitle', 'Connect Klaviyo to see revenue attribution')}
          description={t('analytics.connectKlaviyoDescription', 'Revenue attribution requires an ESP that tracks placed-order events. Connect Klaviyo to see attributed revenue, campaign-vs-flow split, and first-time-vs-returning breakdowns.')}
        />
      ) : null}

      {/* -- Attributed Revenue + Revenue by Message Type (side-by-side) -- */}
      {rev && !rev.available ? null : revenue.isLoading ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <Skeleton variant="rounded" height={500} sx={{ borderRadius: '12px' }} />
          <Skeleton variant="rounded" height={500} sx={{ borderRadius: '12px' }} />
        </Box>
      ) : rev ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <SectionCard title={t('analytics.attributedRevenue', 'Attributed Revenue')}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 3 }}>
              <MetricCard variant="inline" title={t('analytics.attributedRevenue', 'Attributed Revenue')} metric={rev.attributedRevenue} format="currency" />
              <MetricCard variant="inline" title={t('analytics.totalStoreRevenue', 'Total Store Revenue')} metric={rev.totalStoreRevenue} format="currency" />
              <MetricCard variant="inline" title={t('analytics.percentOfTotal', 'Percent of Total')} metric={rev.percentOfTotal} format="percent" />
            </Box>
            {attributedRevenueSeries.length > 0 && (
              <TimeSeriesChart
                variant="inline"
                data={attributedRevenueSeries}
                series={[
                  { dataKey: 'revenue', name: t('analytics.attributedRevenue', 'Attributed Revenue'), type: 'bar' },
                  {
                    dataKey: 'percentAttributed',
                    name: t('analytics.percentAttributed', 'Percent Attributed'),
                    type: 'line',
                    yAxisId: 'right',
                    dot: true,
                  },
                ]}
                formatXAxis={formatBucketLabel}
                formatTooltip={(value, name) =>
                  name === t('analytics.percentAttributed', 'Percent Attributed')
                    ? `${value.toFixed(1)}%`
                    : `$${value.toLocaleString()}`
                }
                formatLeftAxis={(v) => `$${v.toLocaleString()}`}
                formatRightAxis={(v) => `${v.toFixed(0)}%`}
              />
            )}
          </SectionCard>
          <SectionCard title={t('analytics.attributedRevenueByType', 'Attributed Revenue by Message Type')}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
              <MetricCard variant="inline" title={t('analytics.campaignRevenue', 'Campaign Revenue')} metric={rev.revenueByType.campaign} format="currency" />
              <MetricCard variant="inline" title={t('analytics.flowRevenue', 'Flow Revenue')} metric={rev.revenueByType.flow} format="currency" />
            </Box>
            {revChart && revChart.timeSeries.length > 0 && (
              <TimeSeriesChart
                variant="inline"
                data={revChart.timeSeries}
                series={[
                  { dataKey: 'campaignRevenue', name: t('analytics.campaign', 'Campaign'), type: 'bar', stackId: 'revenue' },
                  { dataKey: 'flowRevenue', name: t('analytics.flow', 'Flow'), type: 'bar', stackId: 'revenue' },
                ]}
                formatXAxis={formatBucketLabel}
                formatTooltip={(value) => `$${value.toLocaleString()}`}
                formatLeftAxis={(v) => `$${v.toLocaleString()}`}
              />
            )}
          </SectionCard>
        </Box>
      ) : null}

      {/* -- Revenue by Channel + First-Time vs Returning (side-by-side) -- */}
      {rev && !rev.available ? null : revenue.isLoading || flows.isLoading ? (
        <SectionSkeleton cols={2} height={400} />
      ) : rev ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <SectionCard title={t('analytics.revenueByChannel', 'Revenue by Channel')}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
              <MetricCard variant="inline" title={t('analytics.emailRevenue', 'Email Revenue')} metric={rev.revenueByChannel.email} format="currency" />
              <MetricCard variant="inline" title={t('analytics.smsRevenue', 'SMS Revenue')} metric={rev.revenueByChannel.sms} format="currency" />
            </Box>
            {channelRevenueSeries.length > 0 && (
              <TimeSeriesChart
                variant="inline"
                height={280}
                data={channelRevenueSeries}
                series={[
                  { dataKey: 'emailRevenue', name: t('analytics.email', 'Email'), type: 'bar', stackId: 'channel' },
                  { dataKey: 'smsRevenue', name: t('analytics.sms', 'SMS'), type: 'bar', stackId: 'channel' },
                ]}
                formatXAxis={formatBucketLabel}
                formatTooltip={(value) => `$${value.toLocaleString()}`}
                formatLeftAxis={(v) => `$${v.toLocaleString()}`}
              />
            )}
          </SectionCard>
          <SectionCard title={t('analytics.firstTimeVsReturning', 'First-Time vs Returning')}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
              <MetricCard variant="inline" title={t('analytics.firstTimeCustomer', 'First-Time Customer')} metric={rev.firstTimeVsReturning.firstTime} format="currency" />
              <MetricCard variant="inline" title={t('analytics.returningCustomer', 'Returning Customer')} metric={rev.firstTimeVsReturning.returning} format="currency" />
            </Box>
            {revChart && revChart.firstTimeVsReturning.series.length > 0 ? (
              <TimeSeriesChart
                variant="inline"
                height={280}
                data={revChart.firstTimeVsReturning.series}
                series={[
                  { dataKey: 'firstTime', name: t('analytics.firstTime', 'First-Time'), type: 'bar', stackId: 'fvr' },
                  { dataKey: 'returning', name: t('analytics.returning', 'Returning'), type: 'bar', stackId: 'fvr' },
                ]}
                formatXAxis={formatBucketLabel}
                formatTooltip={(value) => `$${value.toLocaleString()}`}
                formatLeftAxis={(v) => `$${v.toLocaleString()}`}
              />
            ) : (
              <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {t('analytics.noRevenueInPeriod', 'No revenue in this period')}
                </Typography>
              </Box>
            )}
          </SectionCard>
        </Box>
      ) : null}

      {/* -- Campaign Performance + Flow Performance (side-by-side) -- */}
      {campaigns.isLoading || flows.isLoading ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <Skeleton variant="rounded" height={500} sx={{ borderRadius: '12px' }} />
          <Skeleton variant="rounded" height={500} sx={{ borderRadius: '12px' }} />
        </Box>
      ) : (camp || flow) ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          {camp ? (
            <SectionCard title={t('analytics.campaignPerformance', 'Campaign Performance')}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 11 }}>
                {t('analytics.email', 'Email')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
                <MetricCard variant="inline" title={t('analytics.revenue', 'Revenue')} metric={camp.kpis.emailRevenue} format="currency" />
                <MetricCard variant="inline" title={t('analytics.deliveries', 'Deliveries')} metric={camp.kpis.emailDeliveries} />
                <MetricCard
                  variant="inline"
                  title={t('analytics.revenuePerRecipient', 'Revenue Per Recipient')}
                  metric={computeRprMetric(camp.kpis.emailRevenue, camp.kpis.emailDeliveries)}
                  format="currency"
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 11 }}>
                {t('analytics.sms', 'SMS')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
                <MetricCard variant="inline" title={t('analytics.revenue', 'Revenue')} metric={camp.kpis.smsRevenue} format="currency" />
                <MetricCard variant="inline" title={t('analytics.deliveries', 'Deliveries')} metric={camp.kpis.smsDeliveries} />
                <MetricCard
                  variant="inline"
                  title={t('analytics.revenuePerRecipient', 'Revenue Per Recipient')}
                  metric={computeRprMetric(camp.kpis.smsRevenue, camp.kpis.smsDeliveries)}
                  format="currency"
                />
              </Box>
              {campChart && campChart.timeSeries.length > 0 && (
                <TimeSeriesChart
                  variant="inline"
                  height={300}
                  data={campChart.timeSeries}
                  series={[
                    { dataKey: 'revenue', name: t('analytics.revenue', 'Revenue'), type: 'bar' },
                    { dataKey: 'deliveries', name: t('analytics.deliveries', 'Deliveries'), type: 'line', yAxisId: 'right', dot: false },
                  ]}
                  formatXAxis={formatBucketLabel}
                  formatTooltip={(value, name) =>
                    name === t('analytics.revenue', 'Revenue') ? `$${value.toLocaleString()}` : value.toLocaleString()
                  }
                  formatLeftAxis={(v) => `$${v.toLocaleString()}`}
                  formatRightAxis={(v) => v.toLocaleString()}
                />
              )}
            </SectionCard>
          ) : null}
          {flow ? (
            <SectionCard title={t('analytics.flowPerformance', 'Flow Performance')}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 11 }}>
                {t('analytics.email', 'Email')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
                <MetricCard variant="inline" title={t('analytics.revenue', 'Revenue')} metric={flow.kpis.emailRevenue} format="currency" />
                <MetricCard variant="inline" title={t('analytics.deliveries', 'Deliveries')} metric={flow.kpis.emailDeliveries} />
                <MetricCard
                  variant="inline"
                  title={t('analytics.revenuePerRecipient', 'Revenue Per Recipient')}
                  metric={computeRprMetric(flow.kpis.emailRevenue, flow.kpis.emailDeliveries)}
                  format="currency"
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 11 }}>
                {t('analytics.sms', 'SMS')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
                <MetricCard variant="inline" title={t('analytics.revenue', 'Revenue')} metric={flow.kpis.smsRevenue} format="currency" />
                <MetricCard variant="inline" title={t('analytics.deliveries', 'Deliveries')} metric={flow.kpis.smsDeliveries} />
                <MetricCard
                  variant="inline"
                  title={t('analytics.revenuePerRecipient', 'Revenue Per Recipient')}
                  metric={computeRprMetric(flow.kpis.smsRevenue, flow.kpis.smsDeliveries)}
                  format="currency"
                />
              </Box>
              {flowChart && flowChart.timeSeries.length > 0 && (
                <TimeSeriesChart
                  variant="inline"
                  height={300}
                  data={flowChart.timeSeries}
                  series={[
                    { dataKey: 'emailRevenue', name: t('analytics.emailRevenue', 'Email Revenue'), type: 'bar', stackId: 'revenue' },
                    { dataKey: 'smsRevenue', name: t('analytics.smsRevenue', 'SMS Revenue'), type: 'bar', stackId: 'revenue' },
                  ]}
                  formatXAxis={formatBucketLabel}
                  formatTooltip={(value) => `$${value.toLocaleString()}`}
                  formatLeftAxis={(v) => `$${v.toLocaleString()}`}
                />
              )}
            </SectionCard>
          ) : null}
        </Box>
      ) : null}

      {/* -- Flow Channel Detail Charts (kept for granular email/sms revenue vs deliveries trend) -- */}
      {flowsChart.isLoading ? (
        <SectionSkeleton cols={2} height={350} />
      ) : flowChart && flowChart.timeSeries.length > 0 ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <SectionCard title={t('analytics.emailFlowRevenueVsDeliveries', 'Email Flow: Revenue vs Deliveries')}>
            <TimeSeriesChart
              variant="inline"
              data={flowChart.timeSeries}
              series={[
                { dataKey: 'emailRevenue', name: t('analytics.revenue', 'Revenue'), type: 'bar' },
                { dataKey: 'emailDeliveries', name: t('analytics.deliveries', 'Deliveries'), type: 'line', yAxisId: 'right', dot: false },
              ]}
              formatXAxis={formatBucketLabel}
              formatTooltip={(value, name) =>
                name === t('analytics.revenue', 'Revenue') ? `$${value.toLocaleString()}` : value.toLocaleString()
              }
              formatLeftAxis={(v) => `$${v.toLocaleString()}`}
              formatRightAxis={(v) => v.toLocaleString()}
            />
          </SectionCard>
          <SectionCard title={t('analytics.smsFlowPerformance', 'SMS Flow Performance')}>
            <TimeSeriesChart
              variant="inline"
              data={flowChart.timeSeries}
              series={[
                { dataKey: 'smsRevenue', name: t('analytics.revenue', 'Revenue'), type: 'bar' },
                { dataKey: 'smsDeliveries', name: t('analytics.deliveries', 'Deliveries'), type: 'line', yAxisId: 'right', dot: false },
              ]}
              formatXAxis={formatBucketLabel}
              formatTooltip={(value, name) =>
                name === t('analytics.revenue', 'Revenue') ? `$${value.toLocaleString()}` : value.toLocaleString()
              }
              formatLeftAxis={(v) => `$${v.toLocaleString()}`}
              formatRightAxis={(v) => v.toLocaleString()}
            />
          </SectionCard>
        </Box>
      ) : null}

      {/* -- Email Subscriber Growth -- */}
      {listGrowth.isLoading ? (
        <SectionSkeleton cols={3} />
      ) : growth ? (
        <SectionCard title={t('analytics.emailSubscriberGrowth', 'Email Subscriber Growth')}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
            <MetricCard variant="inline" title={t('analytics.netSubscribers', 'Net Subscribers')} metric={growth.email.netSubscribers} />
            <MetricCard variant="inline" title={t('analytics.subscribes', 'Subscribes')} metric={growth.email.subscribers} />
            <MetricCard variant="inline" title={t('analytics.unsubscribes', 'Unsubscribes')} metric={growth.email.unsubscribes} invertTrend />
          </Box>
          {growthChart && growthChart.email.timeSeries.length > 0 && (
            <TimeSeriesChart
              variant="inline"
              data={growthChart.email.timeSeries}
              series={[
                { dataKey: 'subscribersAdded', name: t('analytics.subscribed', 'Subscribed'), type: 'bar', color: '#4caf50' },
                { dataKey: 'unsubscribes', name: t('analytics.unsubscribed', 'Unsubscribed'), type: 'bar', color: '#f44336' },
                { dataKey: 'netSubscribers', name: t('analytics.net', 'Net'), type: 'line', dot: false },
              ]}
              formatXAxis={formatBucketLabel}
            />
          )}
        </SectionCard>
      ) : null}

      {/* -- SMS Subscriber Growth -- */}
      {listGrowth.isLoading ? (
        <SectionSkeleton cols={3} />
      ) : growth ? (
        <SectionCard title={t('analytics.smsSubscriberGrowth', 'SMS Subscriber Growth')}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
            <MetricCard variant="inline" title={t('analytics.netSubscribers', 'Net Subscribers')} metric={growth.sms.netSubscribers} />
            <MetricCard variant="inline" title={t('analytics.subscribes', 'Subscribes')} metric={growth.sms.subscribers} />
            <MetricCard variant="inline" title={t('analytics.unsubscribes', 'Unsubscribes')} metric={growth.sms.unsubscribes} invertTrend />
          </Box>
          {growthChart && growthChart.sms.timeSeries.length > 0 && (
            <TimeSeriesChart
              variant="inline"
              data={growthChart.sms.timeSeries}
              series={[
                { dataKey: 'subscribersAdded', name: t('analytics.subscribed', 'Subscribed'), type: 'bar', color: '#4caf50' },
                { dataKey: 'unsubscribes', name: t('analytics.unsubscribed', 'Unsubscribed'), type: 'bar', color: '#f44336' },
                { dataKey: 'netSubscribers', name: t('analytics.net', 'Net'), type: 'line', dot: false },
              ]}
              formatXAxis={formatBucketLabel}
            />
          )}
        </SectionCard>
      ) : null}
      </Stack>
      )}
    </Stack>
  );
}
